import { TipoSedeEnum } from "common/enums/sede.enum";
import { CFValidator } from "common/helpers/common-utils";
import { BooleanValues, TipoDocumenti, TipoSoggetto } from "constants/global";
import * as formik from "formik";
import * as Yup from "yup";

export const { Formik } = formik;

const { TRUE, FALSE } = BooleanValues;

Yup.setLocale({
	mixed: {
		required: "Campo obbligatorio",
	},
	string: {
		email: "Email non valida",
	},
});

Yup.addMethod(Yup.string, "cfValidation", function () {
	return this.test("codice-fiscale", "Il codice fiscale non è valido", function (value) {
		return CFValidator(value);
	});
});

Yup.addMethod(Yup.mixed, "fileRequired", function () {
	return this.test("file-required", "File obbligatorio", function (value) {
		return !!value;
	});
});

declare module "yup" {
	interface StringSchema {
		cfValidation(): StringSchema<string>;
	}

	interface MixedSchema {
		fileRequired(): MixedSchema<any>;
	}
}

export const firstStepSchema = Yup.object().shape({
	firstName: Yup.string().required().min(2, "Minimo 2 caratteri"),
	lastName: Yup.string().required().min(2, "Minimo 2 caratteri"),
	email: Yup.string().email("Email non valida").required(),
	prefix: Yup.string().required(),
	phoneNumber: Yup.string().required().min(7, "Minimo 7 numeri").max(11, "Telefono troppo lungo"),
	privacyCheck: Yup.boolean().isTrue("Campo obbligatorio").required(),
});

export const codiceAttivazioneSchema = Yup.object().shape({
	codiceAttivazione: Yup.string().test(
		"validazione-codice-attivazione",
		"Il codice di attivazione non è valido",
		function (value) {
			const { currentPath } = this.options.context || {};
			const targetPath = "https://onboarding.buffettifinance.com/onboarding/#/start";
			const regexStandard = /^[a-zA-Z]{4}[0-9]{3}$/;
			const regexTest = /^TEST/;

			if (currentPath === targetPath) {
				return regexStandard.test(value || "");
			}
			return regexStandard.test(value || "") || regexTest.test(value || "");
		}
	),

	codiceDealer: Yup.string().matches(/^[0-9]{3}$/, "Il codice dealer non è valido"),
});

//STEP 2
export const presentaAziendaSchema = Yup.object().shape({
	tipoCliente: Yup.string().required(),
	tipoCodice: Yup.string().when("tipoCliente", {
		is: "PersonaFisica",
		then: (schema) => schema.required(),
	}),
	partitaIva: Yup.string().when("tipoCodice", {
		is: "PIVA",
		then: (schema) =>
			schema.required().min(11).max(11, "La Partita IVA non può superare gli 11 caratteri"),
	}),
	codiceFiscale: Yup.string().when("tipoCodice", {
		is: "CF",
		then: (schema) =>
			schema
				.required("Il codice fiscale è obbligatorio")
				.matches(
					/^(?=.*\d{11}$|^[A-Za-z]{6}[0-9]{2}[A-Za-z]{1}[0-9]{2}[A-Za-z]{1}[0-9]{3}[A-Za-z]{1}$)/,
					"Il codice fiscale non è valido"
				)
				.test(
					"is-valid-length",
					"Il codice fiscale deve essere lungo 11 caratteri alfanumerici o 16 caratteri alfabetici",
					(value) => value.length === 11 || (value.length === 16 && /[A-Za-z]/.test(value))
				),
	}),
});

export const datiAziendaSchema = Yup.object().shape({
	denominazione: Yup.string().required("Campo obbligatorio"),
	naturaGiuridica: Yup.string().required("Campo obbligatorio"),
	// insegna: Yup.string().required("Campo obbligatorio"),
	// rea: Yup.string().required("Campo obbligatorio"),
	codiceAteco: Yup.string()
		.when("tipoAzienda", {
			is: "PIVA",
			then: (schema: any) => schema.required(),
		})
		.matches(/^[0-9.]+$/, "Inserire solo numeri e punti")
		.max(8, "Il codice ateco non può superare gli 8 caratteri"),
	LIC_AUT: Yup.mixed().when("codiceAteco", {
		is: "95.25" || "66.19",
		then: (schema) => schema.test("required", "Campo obbligatorio", (val) => !!val),
	}),
	codiceSdi: Yup.string()
		.min(7, "Il codice SDi non può avere meno di 7 caratteri")
		.max(7, "Il codice SDi non può avere più di 7 caratteri"),
	descrizione: Yup.string().required("Campo obbligatorio"),
	centroScommesse: Yup.boolean().required("Campo obbligatorio"),
	SCO_TGI: Yup.mixed().when("centroScommesse", {
		is: true,
		then: (schema) => schema.test("required", "Campo obbligatorio", (val) => !!val),
	}),
	// SCO_LAA: Yup.mixed().when("centroScommesse", {
	// 	is: true,
	// 	then: (schema) => schema.test("required", "Campo obbligatorio", (val) => !!val),
	// }),
	// SCO_DCM: Yup.mixed().when("centroScommesse", {
	// 	is: true,
	// 	then: (schema) => schema.test("required", "Campo obbligatorio", (val) => !!val),
	// }),
	associazione: Yup.boolean().required("Campo obbligatorio"),
	ASS_ATC: Yup.mixed().when("associazione", {
		is: true,
		then: (schema) => schema.test("required", "Campo obbligatorio", (val) => !!val),
	}),
	// ASS_VRL: Yup.mixed().when("associazione", {
	// 	is: true,
	// 	then: (schema) => schema.test("required", "Campo obbligatorio", (val) => !!val),
	// }),
	ASS_API: Yup.mixed().when("associazione", {
		is: true,
		then: (schema) => schema.test("required", "Campo obbligatorio", (val) => !!val),
	}),
	// CONTRATTO_UTENZA_LOCAZIONE: Yup.mixed().when("associazione", {
	// 	is: true,
	// 	then: (schema) => schema.test("required", "Campo obbligatorio", (val) => !!val),
	// }),
});

export const indirizziAziendaSchema = Yup.object().shape({
	indirizziAzienda: Yup.object().shape({
		telefono: Yup.string().required(),
		prefissoTelefono: Yup.string().required(),
		emailAziendale: Yup.string().email().required(),
		pec: Yup.string().email().required(),
		capSedeLegale: Yup.string()
			.required()
			.matches(/^[0-9]{5}$/, "Cap non valido"),
		nazioneSedeLegale: Yup.string().required(),
		provinciaSedeLegale: Yup.string().required(),
		comuneSedeLegale: Yup.string().required(),
		indirizzoSedeLegale: Yup.string().required().min(2, "Minimo 2 caratteri"),
		civicoSedeLegale: Yup.string().required(),
		corrispondenzaCartacea: Yup.boolean().required(),
		capCorrispondenza: Yup.string()
			.matches(/^[0-9]{5}$/, "Cap non valido")
			.when("corrispondenzaCartacea", {
				is: true,
				then: (schema) => schema.required("Campo obbligatorio"),
			}),
		nazioneCorrispondenza: Yup.string().when("corrispondenzaCartacea", {
			is: true,
			then: (schema) => schema.required("Campo obbligatorio"),
		}),
		provinciaCorrispondenza: Yup.string().when("corrispondenzaCartacea", {
			is: true,
			then: (schema) => schema.required("Campo obbligatorio"),
		}),
		comuneCorrispondenza: Yup.string().when("corrispondenzaCartacea", {
			is: true,
			then: (schema) => schema.required("Campo obbligatorio"),
		}),
		indirizzoCorrispondenza: Yup.string().when("corrispondenzaCartacea", {
			is: true,
			then: (schema) => schema.required("Campo obbligatorio"),
		}),
		civicoCorrispondenza: Yup.string().when("corrispondenzaCartacea", {
			is: true,
			then: (schema) => schema.required("Campo obbligatorio"),
		}),
	}),
	elencoSedi: Yup.array().of(
		Yup.object().shape({
			provincia: Yup.string().when(["operativa", "tipoSede"], {
				is: (operativa: boolean, tipoSede: TipoSedeEnum) =>
					operativa && tipoSede == TipoSedeEnum.ALTRA_SEDE_OPERATIVA,
				then: (s) => s.required(),
			}),
			comune: Yup.string().when(["operativa", "tipoSede"], {
				is: (operativa: boolean, tipoSede: TipoSedeEnum) =>
					operativa && tipoSede == TipoSedeEnum.ALTRA_SEDE_OPERATIVA,
				then: (s) => s.required(),
			}),
			cap: Yup.string().when(["operativa", "tipoSede"], {
				is: (operativa: boolean, tipoSede: TipoSedeEnum) =>
					operativa && tipoSede == TipoSedeEnum.ALTRA_SEDE_OPERATIVA,
				then: (s) => s.required(),
			}),
			nazione: Yup.string().when(["operativa", "tipoSede"], {
				is: (operativa: boolean, tipoSede: TipoSedeEnum) =>
					operativa && tipoSede == TipoSedeEnum.ALTRA_SEDE_OPERATIVA,
				then: (s) => s.required(),
			}),
			indirizzo: Yup.string().when(["operativa", "tipoSede"], {
				is: (operativa: boolean, tipoSede: TipoSedeEnum) =>
					operativa && tipoSede == TipoSedeEnum.ALTRA_SEDE_OPERATIVA,
				then: (s) => s.required(),
			}),
			numeroCivico: Yup.string().when(["operativa", "tipoSede"], {
				is: (operativa: boolean, tipoSede: TipoSedeEnum) =>
					operativa && tipoSede == TipoSedeEnum.ALTRA_SEDE_OPERATIVA,
				then: (s) => s.required(),
			}),
			nomeSoggettoPreposto: Yup.string().when(["operativa", "prepostoIsRL", "tipoSede"], {
				is: (operativa: boolean, prepostoIsRL: BooleanValues) => operativa && prepostoIsRL == FALSE,
				then: (s) => s.required(),
			}),
			cognomeSoggettoPreposto: Yup.string().when(["operativa", "prepostoIsRL", "tipoSede"], {
				is: (operativa: boolean, prepostoIsRL: BooleanValues, tipoSede: TipoSedeEnum) =>
					operativa && prepostoIsRL == FALSE,
				then: (s) => s.required(),
			}),
			emailSoggettoPreposto: Yup.string()
				.email()
				.when(["operativa", "prepostoIsRL", "tipoSede"], {
					is: (operativa: boolean, prepostoIsRL: BooleanValues, tipoSede: TipoSedeEnum) =>
						operativa && prepostoIsRL == FALSE,
					then: (s) => s.required(),
				}),
			telefonoSoggettoPreposto: Yup.string().when(["operativa", "prepostoIsRL", "tipoSede"], {
				is: (operativa: boolean, prepostoIsRL: BooleanValues, tipoSede: TipoSedeEnum) =>
					operativa && prepostoIsRL == FALSE,
				then: (s) =>
					s.matches(/^[1-9][0-9]*$/, "Il numero di cellulare non può iniziare con 0").required(),
			}),
			SCO_CONTRATTO_UTENZA_LOCAZIONEDCM: Yup.mixed().when(["operativa", "tipoSede"], {
				is: (operativa: boolean, tipoSede: TipoSedeEnum) =>
					operativa && tipoSede == TipoSedeEnum.ALTRA_SEDE_OPERATIVA,
				then: (s) => s.fileRequired(),
			}),
		})
	),
});

export const generateIndirizziAziendaSchema = (numSedeOperativa: number) => {
	const sedeOperativaSchema = Yup.object().shape({
		[`${numSedeOperativa}`]: Yup.boolean(),
		[`MatchPrepostoSedeOperativa${numSedeOperativa}`]: Yup.boolean().when(`${numSedeOperativa}`, {
			is: true,
			then: (schema: any) => schema.required("Campo obbligatorio"),
		}),
		nomeSoggettoPreposto: Yup.string().when(`MatchPrepostoSedeOperativa${numSedeOperativa}`, {
			is: false,
			then: (schema: any) => schema.required("Campo obbligatorio"),
		}),
		cognomeSoggettoPreposto: Yup.string().when(`MatchPrepostoSedeOperativa${numSedeOperativa}`, {
			is: false,
			then: (schema: any) => schema.required("Campo obbligatorio"),
		}),
		emailSoggettoPreposto: Yup.string().when(`MatchPrepostoSedeOperativa${numSedeOperativa}`, {
			is: false,
			then: (schema: any) => schema.email("Email non valida").required("Campo obbligatorio"),
		}),
		telefonoSoggettoPreposto: Yup.string().when(`MatchPrepostoSedeOperativa${numSedeOperativa}`, {
			is: false,
			then: (schema: any) =>
				schema.matches(/^[1-9][0-9]*$/, "Il numero di cellulare non può iniziare con 0").required(),
		}),
	});
	return sedeOperativaSchema;
};

export const indirizziAziendaAltroSchema = Yup.object().shape({
	provinciaSedeOperativa: Yup.string().required("Campo obbligatorio"),
	comuneSedeOperativa: Yup.string().required("Campo obbligatorio"),
	capSedeOperativa: Yup.string().required("Campo obbligatorio"),
	indirizzoSedeOperativa: Yup.string().required("Campo obbligatorio"),
	civicoSedeOperativa: Yup.string().required("Campo obbligatorio"),
	SCO_CONTRATTO_UTENZA_LOCAZIONEDCM: Yup.string().required("Campo obbligatorio"),
});

export const datiBilancioSchema: any = Yup.object().shape({
	startup: Yup.boolean().required("Campo obbligatorio"),
	contabilitaSemplificata: Yup.boolean().required("Campo obbligatorio"),
	dataInizioAttivita: Yup.date()
		.required("Campo obbligatorio")
		.max(new Date(), "La data non può essere successiva a oggi"),
	risultatoEconomicoUltimoBilancio: Yup.string().when("startup", {
		is: false,
		then: (schema: any) => schema.required("Campo obbligatorio"),
	}),
	importoUltimoBilancio: Yup.number().when("startup", {
		is: false,
		then: (schema) =>
			schema.required("Campo obbligatorio").notOneOf([0], "Il valore non può essere 0"), // Blocca il valore 0
		otherwise: (schema) => schema.notRequired(),
	}),

	volumeAffari: Yup.string().when("startup", {
		is: false,
		then: (schema: any) => schema.required("Campo obbligatorio"),
	}),
});

export const dettaglioAziendaSchema = Yup.object().shape({
	fondiPubblici: Yup.boolean().required("Campo obbligatorio"),
	tipoFondi: Yup.string().when("fondiPubblici", {
		is: true,
		then: (schema: any) => schema.required("Campo obbligatorio"),
	}),
	tipologiaAttivita: Yup.string().required("Campo obbligatorio"),
	tipologiaAttivitaDescrizione: Yup.string().when("tipologiaAttivita", {
		is: (val: any) => val === "altro" || val === "noProfit",
		then: (schema: any) => schema.required("Campo obbligatorio"),
	}),
	areaGeografica: Yup.string().required("Campo obbligatorio"),
	settoreAttivita: Yup.string().required("Campo obbligatorio"),
	settoreAttivitaDescrizione: Yup.string().when("settoreAttivita", {
		is: "Altro",
		then: (schema: any) => schema.required("Campo obbligatorio"),
	}),
	numeroDipendenti: Yup.number()
		.positive("Solo numeri positivi")
		.min(0, "Non è possibile inserire numeri negativi")
		.required("Campo obbligatorio"),
});

// Step 3

export const anagraficaSchema = Yup.object().shape({
	nome: Yup.string()
		.matches(
			/^[a-zA-Z\'-\s]+$/,
			"Indicare il dato senza caratteri speciali, per le lettere accentate usare l'apostrofo."
		)
		.required("Campo obbligatorio"),
	cognome: Yup.string()
		.matches(
			/^[a-zA-Z\'-\s]+$/,
			"Indicare il dato senza caratteri speciali, per le lettere accentate usare l'apostrofo."
		)
		.required("Campo obbligatorio"),

	codiceFiscale: Yup.string()
		.cfValidation()
		.when("tipoSoggetto", {
			is: TipoSoggetto.LR,
			then: (schema) => schema.required("Campo obbligatorio"),
		}),
	genere: Yup.string().required("Campo obbligatorio"),
	conto: Yup.boolean().required("Campo obbligatorio"),
	dataDiNascita: Yup.date()
		.max(new Date(), "La data non può essere successiva a oggi")
		.test("eta", "Devi avere almeno 18 anni", function (birthdate: any) {
			const cutoff = new Date();
			cutoff.setFullYear(cutoff.getFullYear() - 18);
			return birthdate <= cutoff;
		})
		.required("Campo obbligatorio"),
	statoDiNascita: Yup.string().required("Campo obbligatorio"),
	siglaProvinciaDiNascita: Yup.string().when("statoDiNascita", {
		is: "IT",
		then: (schema: any) => schema.required("Campo obbligatorio"),
	}),
	comuneDiNascita: Yup.string().required("Campo obbligatorio"),
	numeroTel: Yup.string().required("Campo obbligatorio"),
	prefissoTel: Yup.string(),
	email: Yup.string()
		.email("Email non valida")
		.when("tipoSoggetto", {
			is: TipoSoggetto.LR,
			then: (schema) => schema.required("Campo obbligatorio"),
		})
		.when("isInvitation", {
			is: true,
			then: (schema) => schema.required("Campo obbligatorio"),
		}),
	capResidenza: Yup.string()
		.required("Campo obbligatorio")
		.matches(/^[0-9]{5}$/, "Cap non valido"),
	nazioneResidenza: Yup.string().required("Campo obbligatorio"),
	siglaProvinciaResidenza: Yup.string().when("nazioneResidenza", {
		is: "IT",
		then: (schema: any) => schema.required("Campo obbligatorio"),
	}),
	comuneResidenza: Yup.string().required("Campo obbligatorio"),
	indirizzoResidenza: Yup.string().required("Campo obbligatorio"),
	civicoResidenza: Yup.string().required("Campo obbligatorio"),
});

export const carichePubblicheSchema = Yup.object().shape({
	hasCarichePubbliche: Yup.boolean().required("Campo obbligatorio"),
	caricaRicoperta: Yup.string().when("hasCarichePubbliche", {
		is: true,
		then: (schema: any) => schema.required("Campo obbligatorio"),
	}),
	fatca: Yup.boolean().required("Campo obbligatorio"),
	hasCarichiPendenti: Yup.boolean().when("tipoSoggetto", {
		is: TipoSoggetto.LR,
		then: (schema: any) => schema.required("Campo obbligatorio"),
	}),
	autoritaEmanante: Yup.string().when("hasCarichiPendenti", {
		is: true,
		then: (schema: any) => schema.required("Campo obbligatorio"),
	}),
	oggettoProvvedimento: Yup.string().when("hasCarichiPendenti", {
		is: true,
		then: (schema: any) => schema.required("Campo obbligatorio"),
	}),
	dataEmissione: Yup.date()
		.when("hasCarichiPendenti", {
			is: true,
			then: (schema: any) => schema.required("Campo obbligatorio"),
		})
		.max(new Date(), "La data non può essere successiva a oggi"),
	statoProvvedimento: Yup.string().when("hasCarichiPendenti", {
		is: true,
		then: (schema: any) => schema.required("Campo obbligatorio"),
	}),
});

export const verificaIdentitaSchema = Yup.object().shape({
	isTesSanVisible: Yup.boolean(),
	tipoDocumento: Yup.string().required(),
	DOC_IDE_FRONT: Yup.mixed().when("tipoDocumento", {
		is: TipoDocumenti.CARTA_IDENTITA,
		then: (schema) => schema.test("required", "Campo obbligatorio", (val) => !!val),
	}),
	DOC_IDE_BACK: Yup.mixed().when("tipoDocumento", {
		is: TipoDocumenti.CARTA_IDENTITA,
		then: (schema) => schema.test("required", "Campo obbligatorio", (val) => !!val),
	}),
	DOC_PAT_FRONT: Yup.mixed().when("tipoDocumento", {
		is: TipoDocumenti.PATENTE,
		then: (schema) => schema.test("required", "Campo obbligatorio", (val) => !!val),
	}),
	DOC_PAT_BACK: Yup.mixed().when("tipoDocumento", {
		is: TipoDocumenti.PATENTE,
		then: (schema) => schema.test("required", "Campo obbligatorio", (val) => !!val),
	}),
	DOC_PAS_FRONT: Yup.mixed().when("tipoDocumento", {
		is: TipoDocumenti.PASSAPORTO,
		then: (schema) => schema.test("required", "Campo obbligatorio", (val) => !!val),
	}),
	DOC_PAS_BACK: Yup.mixed().when("tipoDocumento", {
		is: TipoDocumenti.PASSAPORTO,
		then: (schema) => schema.test("required", "Campo obbligatorio", (val) => !!val),
	}),
	TES_SAN: Yup.mixed().when("isTesSanVisible", {
		is: true,
		then: (schema) => schema.test("required", "Campo obbligatorio", (val) => !!val),
	}),
	numeroDocumento: Yup.string().required("Campo obbligatorio"),
	documentoRilasciatoDa: Yup.string().required("Campo obbligatorio"),
	dataRilascioDocumento: Yup.date()
		.required("Campo obbligatorio")
		.max(new Date(), "La data non può essere successiva a oggi"),
	dataScadenzaDocumento: Yup.date()
		.required("Campo obbligatorio")
		.min(new Date(), "Il documento non può essere scaduto"),
});

// step 5
export const paymentsSchema = Yup.object().shape({
	reagioneSociale: Yup.string().required(),
	iban: Yup.string()
		.required()
		.min(27, "L'Iban non può avere meno di 27 caratteri")
		.max(27, "L'Iban non può superare i 27 caratteri")
		.matches(
			/^[A-Za-z]{2}[0-9]{2}[0-9A-Za-z]{1}[0-9]{10}[0-9A-Za-z]{12}$/,
			"Formato Iban non valido"
		),
	nomeProponente: Yup.string().required(),
	cognomeProponente: Yup.string().required(),
	codiceFiscaleProponente: Yup.string().required(),
	idPagamento: Yup.string(),
});

// step 6
export const FirmaSchema = Yup.object().shape({
	documentazione: Yup.boolean().isTrue("Campo obbligatorio").required(),
	norme: Yup.boolean().isTrue("Campo obbligatorio").required(),
	documentsDownloaded: Yup.boolean().isTrue("Scarica gli allegati").required(),
});
